import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSectionBase } from '../../Constant/useActiveLibrary';
import { REPORT_CATEGORIES, ALL_REPORT_CONFIGS } from './reportConfigs';
import '../lib-page-header.css';
import './Reports.css';
import './ReportsHub.css';

// Presentational only — icon/accent per category name. Every name here must
// match a real REPORT_CATEGORIES entry (reportConfigs.js); an unmapped
// category still renders (falls back to the "blue" look) rather than being
// dropped, so a future category never silently disappears from the hub.
const CATEGORY_META = {
  Circulation: { icon: 'fas fa-exchange-alt', accent: 'blue' },
  Members: { icon: 'fas fa-users', accent: 'indigo' },
  Books: { icon: 'fas fa-book', accent: 'deepblue' },
  Finance: { icon: 'fas fa-wallet', accent: 'green' },
  Reservations: { icon: 'fas fa-bookmark', accent: 'purple' }
};
const DEFAULT_CATEGORY_META = { icon: 'fas fa-folder', accent: 'blue' };

// Derived from the report's own config — never hardcoded per report, so this
// stays accurate if a report's filters/search change later. Every report
// goes through the same ReportPage shell, which always renders PDF/Excel/
// Print actions, so those two are unconditional.
const featureBadgesFor = (report) => {
  const badges = [];
  if (report.searchPlaceholder) badges.push({ label: 'Search', icon: 'fas fa-search' });
  if (report.filters && report.filters.length > 0) badges.push({ label: 'Filters', icon: 'fas fa-filter' });
  badges.push({ label: 'PDF', icon: 'fas fa-file-pdf' });
  badges.push({ label: 'Excel', icon: 'fas fa-file-excel' });
  return badges;
};

const TODAY_LABEL = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * The centralized Reports landing page — replaces the old KPI-only
 * ReportDashboard as the destination for the existing "Reports" sidebar
 * entry. The previous KPI/summary content is NOT removed: it still lives at
 * /librarian/reports/summary (linked below) so no existing functionality is
 * lost — this page is purely a presentational redesign around the exact
 * same REPORT_CATEGORIES/report configs, routes and report keys as before.
 *
 * Search/category-filter below operate only on the report metadata already
 * loaded here (title/category/subtitle) — no API call, no change to any
 * individual report's own filters.
 */
const ReportsHub = () => {
  const sectionBase = useSectionBase();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const totalReports = ALL_REPORT_CONFIGS.length;
  const totalCategories = REPORT_CATEGORIES.length;
  const categoryChips = useMemo(() => ['All', ...REPORT_CATEGORIES.map((c) => c.name)], []);

  const filteredCategories = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return REPORT_CATEGORIES
      .filter((category) => activeCategory === 'All' || category.name === activeCategory)
      .map((category) => ({
        ...category,
        reports: category.reports.filter((report) => {
          if (!term) return true;
          return (
            report.title.toLowerCase().includes(term) ||
            category.name.toLowerCase().includes(term) ||
            (report.subtitle || '').toLowerCase().includes(term)
          );
        })
      }))
      .filter((category) => category.reports.length > 0);
  }, [searchTerm, activeCategory]);

  const hasResults = filteredCategories.length > 0;

  return (
    <div className="container-fluid mt-3 reports-hub">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-chart-bar" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Reports &amp; Analytics</h3>
            <div className="lib-page-header__subtitle">Generate, filter, export and review library operational reports from one centralized workspace.</div>
          </div>
        </div>
        <div className="lib-page-header__actions reports-hub__header-stats">
          <div className="reports-hub__header-stat">
            <span className="reports-hub__header-stat-value">{totalReports}</span>
            <span className="reports-hub__header-stat-label">Total Reports</span>
          </div>
          <div className="reports-hub__header-stat">
            <span className="reports-hub__header-stat-value">{TODAY_LABEL}</span>
            <span className="reports-hub__header-stat-label">Today</span>
          </div>
        </div>
      </div>

      <div className="reports-hub__intro">
        <div className="reports-hub__intro-text">
          <i className="fas fa-info-circle" aria-hidden="true" />
          <div>
            <strong>Centralized Reporting</strong>
            <span>Search, filter, print and export operational library reports from one place.</span>
          </div>
        </div>
        <Link
          to={`${sectionBase}/reports/summary`}
          className="reports-hub__intro-link"
          title="A quick KPI/analytics snapshot — for detailed, filterable, exportable records use the categories below"
        >
          <i className="fas fa-tachometer-alt" aria-hidden="true" /> View Analytics Summary
        </Link>
      </div>

      <div className="reports-hub__stats-strip">
        <div className="reports-hub__stat-card">
          <i className="fas fa-layer-group" aria-hidden="true" />
          <div><strong>{totalReports} Operational Reports</strong><span>Every report below is fully functional</span></div>
        </div>
        <div className="reports-hub__stat-card">
          <i className="fas fa-sitemap" aria-hidden="true" />
          <div><strong>{totalCategories} Report Categories</strong><span>Grouped by operational area</span></div>
        </div>
        <div className="reports-hub__stat-card">
          <i className="fas fa-file-export" aria-hidden="true" />
          <div><strong>PDF · Excel · Print</strong><span>Export formats on every report</span></div>
        </div>
        <div className="reports-hub__stat-card">
          <i className="fas fa-sliders-h" aria-hidden="true" />
          <div><strong>Search &amp; Filters</strong><span>Built into each report</span></div>
        </div>
      </div>

      <div className="reports-hub__toolbar">
        <div className="reports-hub__search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search reports..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search reports"
          />
        </div>
        <div className="reports-hub__chips" role="group" aria-label="Filter reports by category">
          {categoryChips.map((name) => (
            <button
              key={name}
              type="button"
              aria-pressed={activeCategory === name}
              className={`reports-hub__chip${activeCategory === name ? ' is-active' : ''}`}
              onClick={() => setActiveCategory(name)}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {hasResults ? (
        filteredCategories.map((category) => {
          const meta = CATEGORY_META[category.name] || DEFAULT_CATEGORY_META;
          return (
            <section key={category.name} className="reports-hub__section" aria-label={`${category.name} Reports`}>
              <div className="reports-hub__section-header">
                <span className={`reports-hub__section-icon reports-hub__section-icon--${meta.accent}`}>
                  <i className={meta.icon} aria-hidden="true" />
                </span>
                <h4 className="reports-hub__section-title">{category.name} Reports</h4>
                <span className="reports-hub__section-count">{category.reports.length} report{category.reports.length === 1 ? '' : 's'}</span>
              </div>

              <div className="reports-hub__grid">
                {category.reports.map((report) => (
                  <Link
                    key={report.key}
                    to={`${sectionBase}/reports/${report.key}`}
                    className={`reports-hub__card reports-hub__card--${meta.accent}`}
                  >
                    <div className="reports-hub__card-top">
                      <span className="reports-hub__card-badge">{category.name}</span>
                      <span className="reports-hub__card-icon">
                        <i className={report.icon} aria-hidden="true" />
                      </span>
                    </div>

                    <h5 className="reports-hub__card-title">{report.title}</h5>
                    <p className="reports-hub__card-desc">{report.subtitle}</p>

                    <div className="reports-hub__card-features">
                      {featureBadgesFor(report).map((badge) => (
                        <span key={badge.label} className="reports-hub__feature-badge">
                          <i className={badge.icon} aria-hidden="true" />{badge.label}
                        </span>
                      ))}
                    </div>

                    <span className="reports-hub__card-action">
                      Open Report <i className="fas fa-arrow-right" aria-hidden="true" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })
      ) : (
        <div className="reports-hub__empty">
          <i className="fas fa-folder-open" aria-hidden="true" />
          <p>No reports match your search.</p>
        </div>
      )}
    </div>
  );
};

export default ReportsHub;
